import React from 'react';
import { useLiveMetrics } from '../hooks/useLiveMetrics';

// ── Format helpers ────────────────────────────────────────────────────────────
function fmtPrice(v: number | null): string {
  if (v === null || v === 0) return '—';
  if (v >= 1000) return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (v < 100) return '$' + v.toFixed(2);
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtVol(v: number | null): string {
  if (v === null || v === 0) return '—';
  return '$' + (v / 1e9).toFixed(1) + 'B';
}

function fmtPct(v: number | null): string {
  if (v === null) return '—';
  const sign = v >= 0 ? '+' : '';
  return sign + v.toFixed(2) + '%';
}

function fmtYield(v: number | null): string {
  if (v === null) return '—';
  return v.toFixed(2) + '%';
}

function fmtYieldChange(v: number | null): string {
  if (v === null) return '—';
  const sign = v >= 0 ? '+' : '';
  return sign + v.toFixed(2) + '%';
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface TickerItem {
  label: string;
  value: string;
  delta: string;
  positive: boolean;
  isManual?: boolean;
}

// ── Sub-components ────────────────────────────────────────────────────────────
const TickerItemCard: React.FC<{ item: TickerItem }> = ({ item }) => (
  <div className="flex items-center gap-2 px-6 border-r border-white/5 whitespace-nowrap">
    <span className="text-gray-500 text-xs font-medium uppercase tracking-wider">{item.label}</span>
    <span className="text-white text-sm font-semibold">{item.value}</span>
    {item.delta !== '—' ? (
      <span
        className="text-xs font-semibold px-1.5 py-0.5 rounded"
        style={{
          color: item.positive ? '#00C896' : '#FF4444',
          backgroundColor: item.positive ? '#00C89618' : '#FF444418',
        }}
      >
        {item.delta}
      </span>
    ) : item.isManual ? (
      <span
        className="text-xs font-medium px-1.5 py-0.5 rounded"
        style={{ color: '#888', backgroundColor: '#88888818' }}
      >
        —
      </span>
    ) : null}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const TickerBar: React.FC = () => {
  const m = useLiveMetrics();

  const items: TickerItem[] = [
    {
      label: 'BTC',
      value: fmtPrice(m.btcPrice),
      delta: fmtPct(m.btcChange24h),
      positive: m.btcChange24h >= 0,
    },
    {
      label: 'BTC 24h Vol',
      value: fmtVol(m.btcVol24h),
      delta: '—',
      positive: true,
      isManual: true,
    },
    {
      label: 'ETH',
      value: fmtPrice(m.ethPrice),
      delta: fmtPct(m.ethChange24h),
      positive: m.ethChange24h >= 0,
    },
    {
      label: 'S&P 500',
      value: m.spx !== null ? m.spx.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—',
      delta: m.spxChange !== null ? fmtPct(m.spxChange) : '—',
      positive: (m.spxChange ?? 0) >= 0,
      isManual: m.spx === null,
    },
    {
      label: 'Gold',
      value: fmtPrice(m.gold),
      delta: m.goldChange !== null ? fmtPct(m.goldChange) : '—',
      positive: (m.goldChange ?? 0) >= 0,
      isManual: m.gold === null,
    },
    {
      label: 'DXY',
      value: m.dxy !== null ? m.dxy.toFixed(2) : '—',
      delta: m.dxyChange !== null ? fmtPct(m.dxyChange) : '—',
      positive: (m.dxyChange ?? 0) >= 0,
      isManual: m.dxy === null,
    },
    {
      label: '10Y Yield',
      value: fmtYield(m.tenYield),
      delta: m.tenYieldChange !== null ? fmtYieldChange(m.tenYieldChange) : '—',
      positive: (m.tenYieldChange ?? 0) >= 0,
      isManual: m.tenYield === null,
    },
  ];

  // Duplicate for seamless loop
  const allItems = [...items, ...items];

  // Last-updated label
  const updatedLabel = m.lastUpdated
    ? m.lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div
      className="w-full border-t border-b border-white/5 overflow-hidden relative"
      style={{ background: '#0D0D0D', height: '44px', minHeight: '44px', maxHeight: '44px', overflow: 'hidden' }}
    >
      <div
        className="flex items-center h-full animate-marquee"
        style={{ width: 'max-content' }}
      >
        {allItems.map((item, i) => (
          <TickerItemCard key={i} item={item} />
        ))}
      </div>

      {/* LIVE badge + timestamp pinned to the right */}
      <div
        className="absolute right-0 top-0 h-full flex items-center gap-2 px-3"
        style={{ background: 'linear-gradient(to right, transparent, #0D0D0D 30%)' }}
      >
        {m.loading && (
          <span className="text-gray-600 text-xs">Loading…</span>
        )}
        {!m.loading && updatedLabel && (
          <span className="text-gray-600 text-xs hidden sm:inline">{updatedLabel}</span>
        )}
        <div className="flex items-center gap-1">
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{
              backgroundColor: m.loading ? '#888' : '#00C896',
              animation: m.loading ? 'none' : 'pulse 2s infinite',
            }}
          />
          <span
            className="text-xs font-semibold"
            style={{ color: m.loading ? '#888' : '#00C896' }}
          >
            {m.loading ? 'LOADING' : 'LIVE'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TickerBar;
